/**
 * @fileoverview Customer-to-customer wash-credit send flow.
 *
 * Owner picker:
 *   • Defaults to owners the customer has previously transacted with
 *     (/api/me/transacted-owners) so we don't dump the global directory
 *     on someone with one favorite car wash.
 *   • Also supports search across all active owners (/api/owners/search?q=)
 *     for first-time gifts to a wash the customer hasn't paid yet.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Input from '../../../src/components/Input';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';

interface TransactedOwner { id: string; businessName: string; txCount: number; lastTxAt: string }
interface SearchResult { id: string; businessName: string }

const PRESET_AMOUNTS_CENTS = [1000, 2000, 5000, 10000]; // $10, $20, $50, $100

export default function SendGiftScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [transacted, setTransacted] = useState<TransactedOwner[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchActive, setSearchActive] = useState(false);
  const [ownerId, setOwnerId] = useState<string>('');
  const [ownerName, setOwnerName] = useState<string>('');

  const [recipientEmail, setRecipientEmail] = useState('');
  const [amountCents, setAmountCents] = useState(2000);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const list = await apiGet<TransactedOwner[]>('/api/me/transacted-owners');
        if (ac.signal.aborted) return;
        setTransacted(list);
        if (list.length === 1) { setOwnerId(list[0].id); setOwnerName(list[0].businessName); }
      } catch (err: any) {
        if (!ac.signal.aborted) toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    })();
    return () => ac.abort();
  }, []);

  // Debounced search query — runs at 300ms idle
  useEffect(() => {
    if (!searchActive) return;
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await apiGet<{ results: SearchResult[] }>(`/api/owners/search?q=${encodeURIComponent(q)}`);
        setSearchResults(res.results);
      } catch { /* silent */ }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, searchActive]);

  const finalAmountCents = customAmount
    ? Math.max(500, Math.min(50000, Math.round(parseFloat(customAmount) * 100) || 0))
    : amountCents;

  const handleSubmit = async () => {
    if (!ownerId) {
      toast.show({ type: 'error', title: 'Error', message: 'Pick a car wash' });
      return;
    }
    if (!recipientEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a valid recipient email' });
      return;
    }
    if (finalAmountCents < 500 || finalAmountCents > 50000) {
      toast.show({ type: 'error', title: 'Error', message: 'Amount must be $5–$500' });
      return;
    }

    try {
      setSubmitting(true);
      await apiPost('/api/wash-credit/send', {
        ownerId,
        recipientEmail: recipientEmail.trim().toLowerCase(),
        amountCents: finalAmountCents,
        message: message.trim() || undefined,
      });
      toast.show({ type: 'success', title: 'Gift sent', message: `${recipientEmail} will get an email shortly.` });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const pickOwner = (id: string, name: string) => {
    setOwnerId(id);
    setOwnerName(name);
    setSearchActive(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Recipient</Text>
          <Input
            label="Email"
            value={recipientEmail}
            onChangeText={setRecipientEmail}
            placeholder="friend@example.com"
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
          />
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Car Wash</Text>
          {ownerId && !searchActive ? (
            <View style={styles.selectedOwnerRow}>
              <Ionicons name="checkmark-circle" size={20} color={colors.success} />
              <Text style={styles.selectedOwnerText}>{ownerName}</Text>
              <TouchableOpacity onPress={() => { setOwnerId(''); setOwnerName(''); }}>
                <Text style={{ color: colors.primary, fontSize: fontSize.sm }}>Change</Text>
              </TouchableOpacity>
            </View>
          ) : !searchActive ? (
            <>
              {transacted.length === 0 ? (
                <Text style={styles.hintText}>You haven't transacted with any car washes yet — use search to pick one.</Text>
              ) : (
                <>
                  <Text style={styles.subLabel}>From your history</Text>
                  {transacted.map(o => (
                    <TouchableOpacity key={o.id} onPress={() => pickOwner(o.id, o.businessName)} style={styles.ownerRow}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.ownerName}>{o.businessName}</Text>
                        <Text style={styles.ownerSub}>{o.txCount} wash{o.txCount === 1 ? '' : 'es'} · last {new Date(o.lastTxAt).toLocaleDateString()}</Text>
                      </View>
                      <Ionicons name="chevron-forward" size={18} color={colors.textLight} />
                    </TouchableOpacity>
                  ))}
                </>
              )}
              <TouchableOpacity onPress={() => setSearchActive(true)} style={styles.searchTrigger}>
                <Ionicons name="search" size={16} color={colors.primary} />
                <Text style={{ color: colors.primary, fontSize: fontSize.sm }}>Search for a different car wash</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <Input
                label="Search by name"
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Kraken, Sparkle, etc."
                autoCapitalize="none"
                autoCorrect={false}
              />
              {searchQuery.trim().length < 2 ? (
                <Text style={styles.hintText}>Type at least 2 letters to search.</Text>
              ) : searchResults.length === 0 ? (
                <Text style={styles.hintText}>No matches.</Text>
              ) : (
                searchResults.map(o => (
                  <TouchableOpacity key={o.id} onPress={() => pickOwner(o.id, o.businessName)} style={styles.ownerRow}>
                    <Text style={styles.ownerName}>{o.businessName}</Text>
                    <Ionicons name="chevron-forward" size={18} color={colors.textLight} />
                  </TouchableOpacity>
                ))
              )}
              <TouchableOpacity onPress={() => { setSearchActive(false); setSearchQuery(''); setSearchResults([]); }}>
                <Text style={{ color: colors.textSecondary, fontSize: fontSize.sm, marginTop: spacing.sm }}>Cancel search</Text>
              </TouchableOpacity>
            </>
          )}
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Amount</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
            {PRESET_AMOUNTS_CENTS.map(amt => (
              <TouchableOpacity
                key={amt}
                onPress={() => { setAmountCents(amt); setCustomAmount(''); }}
                style={[styles.chip, !customAmount && amountCents === amt && { backgroundColor: colors.primary, borderColor: colors.primary }]}
              >
                <Text style={[styles.chipText, !customAmount && amountCents === amt && { color: '#fff' }]}>
                  ${(amt / 100).toFixed(0)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <Input
            label="Custom amount ($5–$500)"
            value={customAmount}
            onChangeText={setCustomAmount}
            placeholder="e.g. 25"
            keyboardType="decimal-pad"
          />
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Personal Message (optional)</Text>
          <Input
            value={message}
            onChangeText={setMessage}
            placeholder='e.g. "Treat yourself to a wash on me!"'
            multiline
            maxLength={500}
            numberOfLines={3}
          />
        </Card>

        <Card style={[styles.formCard, { backgroundColor: colors.primaryBg, padding: spacing.md }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            <Ionicons name="card" size={22} color={colors.primary} />
            <View style={{ flex: 1 }}>
              <Text style={{ color: colors.text, fontWeight: fontWeight.semibold as any }}>
                You'll pay ${(finalAmountCents / 100).toFixed(2)}
              </Text>
              <Text style={{ color: colors.textSecondary, fontSize: fontSize.xs, marginTop: 2 }}>
                Charged to your default card. Recipient gets the full amount as wash credit.
              </Text>
            </View>
          </View>
        </Card>

        <Button title="Send Gift" onPress={handleSubmit} loading={submitting} size="lg" style={{ marginTop: spacing.sm }} />
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  formCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text, marginBottom: spacing.md },
  subLabel: { fontSize: fontSize.xs, color: c.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: spacing.xs },
  hintText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic', textAlign: 'center', padding: spacing.sm },
  chip: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  chipText: { fontSize: fontSize.sm, color: c.text },
  ownerRow: { flexDirection: 'row', alignItems: 'center', padding: spacing.md, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  ownerName: { fontSize: fontSize.md, color: c.text, fontWeight: fontWeight.semibold as any, flex: 1 },
  ownerSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  searchTrigger: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, padding: spacing.sm, marginTop: spacing.sm },
  selectedOwnerRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md, backgroundColor: c.success + '15', borderRadius: borderRadius.md },
  selectedOwnerText: { flex: 1, fontSize: fontSize.md, color: c.text, fontWeight: fontWeight.semibold as any },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
